/**
 * 华丰达 2.4G 接收器 PID 0xfef9（65273）与 identifier 前缀 3141:65273:
 * X87 V2、X87 Neon、X75GC 等多款共用同一 VID/PID，必须靠全量 identifier 消歧，
 * 勿在型号文件里互相 import，也勿仅凭 displayname 区分 Neon / V2。
 */

export const HFD_FEF9_DONGLE_PRODUCT_ID = 0xfef9;
export const HFD_FEF9_DONGLE_ID_PREFIX = "3141:65273:";

/**
 * X87 Neon 2.4G 唯一标识（配置表 / SDK getDeviceId 全量 id）。
 * 与 V2 的裸 id `3141:65273:2.4G Dongle` 区分，必须带 `:3:10`。
 */
export const HFD_FEF9_DONGLE_X87_NEON_IDENTIFIER =
  "3141:65273:2.4G Dongle:3:10";

export type HfdFef9DongleEquipmentLike = {
  productId?: number;
  displayname?: string;
  productName?: string;
  keyboardType?: string;
  identifier?: string;
};

export function isHfdFef9DongleProductId(pid: unknown): boolean {
  return Number(pid) === HFD_FEF9_DONGLE_PRODUCT_ID;
}

function fef9DongleNameBlob(eq: HfdFef9DongleEquipmentLike): string {
  return `${eq.displayname ?? ""} ${eq.productName ?? ""} ${eq.keyboardType ?? ""}`;
}

/**
 * 是否为 X87 Neon 的 fef9 identifier。
 * 只认全量 `3141:65273:2.4G Dongle:3:10`（及 SDK 后续追加后缀），不靠名称。
 */
export function isHfdFef9DongleX87NeonIdentifier(identifier: unknown): boolean {
  const id = String(identifier ?? "").trim();
  if (!id) return false;
  return (
    id === HFD_FEF9_DONGLE_X87_NEON_IDENTIFIER ||
    id.startsWith(`${HFD_FEF9_DONGLE_X87_NEON_IDENTIFIER}:`)
  );
}

/** 共用 dongle 上命中 X75GC（正向规则，不依赖 X87 V2 / Neon） */
export function isHfdFef9DongleX75Gc(
  eq: HfdFef9DongleEquipmentLike | null | undefined
): boolean {
  if (!eq || !isHfdFef9DongleProductId(eq.productId)) return false;
  const id = String(eq.identifier ?? "").trim();
  if (id.includes("X75GC")) return true;
  if (id.startsWith(`${HFD_FEF9_DONGLE_ID_PREFIX}X75GC`)) return true;
  return /\bx75\s*gc\b/i.test(fef9DongleNameBlob(eq));
}

/**
 * 共用 dongle 上命中 X87 Neon。
 * 与 V2 同 VID/PID，仅当 identifier 为 `3141:65273:2.4G Dongle:3:10` 时成立。
 */
export function isHfdFef9DongleX87Neon(
  eq: HfdFef9DongleEquipmentLike | null | undefined
): boolean {
  if (!eq || !isHfdFef9DongleProductId(eq.productId)) return false;
  return isHfdFef9DongleX87NeonIdentifier(eq.identifier);
}

/** 共用 dongle 上命中 X87 V2（正向规则；须排除 X75GC / X87 Neon 全量 id） */
export function isHfdFef9DongleX87V2(
  eq: HfdFef9DongleEquipmentLike | null | undefined
): boolean {
  if (!eq || !isHfdFef9DongleProductId(eq.productId)) return false;
  const id = String(eq.identifier ?? "").trim();
  const nameBlob = fef9DongleNameBlob(eq);
  if (id.includes("X75GC") || /\bx75\s*gc\b/i.test(nameBlob)) return false;
  if (isHfdFef9DongleX87NeonIdentifier(id)) return false;
  if (/\bx87\s*v2\b/i.test(nameBlob) || id.includes("X87 V2")) return true;
  if (
    id.startsWith(HFD_FEF9_DONGLE_ID_PREFIX) &&
    (id.includes("2.4G Dongle") || id.includes("Dongle"))
  ) {
    return true;
  }
  if (id.startsWith(HFD_FEF9_DONGLE_ID_PREFIX) && !id.includes("X75GC")) {
    return true;
  }
  // 仅 PID、无型号信息：默认不判为 X87 V2，交给配置表 / 连接流消歧
  return false;
}
